import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { buildBreadcrumb, resolvePageName } from "../nav/breadcrumb.tsx";
import { IssaQuickChat } from "../../pages/issaInsights/issaQuickChat.tsx";
import { buildPageHelpPrompt, isPageHelpAvailable } from "../../pages/issaInsights/issaPageHelp.ts";
import { StatusIcon } from "./statusIcon.tsx";
import { useAssistant } from "../../config/assistantProfile.ts";
import styles from "./pageHelpButton.module.scss";

/**
 * Discreet "Ask Issa about this page" affordance in the context bar, next to the
 * breadcrumb and the favourite star: a muted info icon, styled like the star (always
 * visible, stronger on hover/focus, never hover-only), so it stays keyboard reachable
 * and visible on touch. Works for every route kind because it resolves the page the
 * same way the breadcrumb does, and opens the quick chat with the page's exact internal
 * name so the documentation tool can find it. The chat picks its usual model for a new
 * conversation (Analyst where available, else Guide). Hidden for Ingress-only users
 * (who have no Issa), like the topbar pill.
 *
 * The tooltip is our own, not the browser `title`: it shows immediately on hover and
 * keyboard focus, in the dropdown-menu surface style, and is portalled to the body
 * because the breadcrumb wrapper clips overflow.
 */
export function PageHelpButton() {
  const { pathname, search } = useLocation();
  const { cache } = useAuth();
  const assistant = useAssistant();
  const label = `Ask ${assistant.name} about this page`;
  const [open, setOpen] = useState(false);
  const [tip, setTip] = useState<{ left: number; top: number; clamped: boolean } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  // The button sits at the far right of the context bar, so a tooltip centred under it
  // runs off the viewport. Measure after layout (before paint) and shift it back inside —
  // ONCE per show. Re-measuring after each shift looped until React's update-depth cap
  // (#185, "Maximum update depth exceeded"): translateX(-50%) and sub-pixel snapping can
  // leave the edge a fraction of a pixel out however often it is nudged, and a tooltip
  // wider than the viewport ping-pongs between the two edge rules.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el || !tip || tip.clamped) return;
    const margin = 8;
    const rect = el.getBoundingClientRect();
    let shift = 0;
    if (rect.right > window.innerWidth - margin) shift = window.innerWidth - margin - rect.right;
    if (rect.left + shift < margin) shift = margin - rect.left; // too wide: pin the start edge
    setTip((t) => (t ? { ...t, left: t.left + shift, clamped: true } : t));
  }, [tip]);

  const pageName = resolvePageName(pathname, search);
  const pages = useMemo(() => cache?.PageInfo?.Page ?? [], [cache]);
  const menuItems = useMemo(() => cache?.PageInfo?.MenuItem ?? [], [cache]);

  const help = useMemo(() => {
    if (!pageName || !isPageHelpAvailable(pageName)) return null;
    const page = pages.find((p) => p.Name?.toLowerCase() === pageName.toLowerCase());
    return buildPageHelpPrompt({
      pageName,
      displayName: page?.DisplayName,
      trail: buildBreadcrumb(pageName, pages, menuItems),
    });
  }, [pageName, pages, menuItems]);

  const showTip = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setTip({ left: r.left + r.width / 2, top: r.bottom + 6, clamped: false });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);

  if (!help || cache?.CommonData?.IngressMembership) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={styles.helpBtn}
        onClick={() => { hideTip(); setOpen(true); }}
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onFocus={showTip}
        onBlur={hideTip}
        aria-label={label}
      >
        {/* The info kind carries the status-blue token by default; inherit the button's
            muted colour instead so this reads as a control, not a status. */}
        <StatusIcon kind="info" size={16} style={{ color: "inherit" }} />
      </button>
      {tip && !open && createPortal(
        // aria-hidden: the button already carries the name via aria-label.
        <span ref={tipRef} role="tooltip" aria-hidden="true" className={styles.tooltip} style={{ left: tip.left, top: tip.top }}>
          {label}
        </span>,
        document.body,
      )}
      {open && (
        <IssaQuickChat
          open
          prompt={help.prompt}
          displayPrompt={help.displayPrompt}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
